import type { HTMLAttributes, ReactNode } from "react";

export function Card({ className = "", children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`rounded-xl border border-surface-border bg-surface-card shadow-card ${className}`} {...props}>
      {children}
    </div>
  );
}

type BadgeTone = "neutral" | "success" | "danger" | "warn" | "gold";

const badgeTones: Record<BadgeTone, string> = {
  neutral: "bg-surface-raised text-ink-muted border-surface-border",
  success: "bg-success-bg text-success border-success/30",
  danger: "bg-danger-bg text-danger border-danger/30",
  warn: "bg-warn-bg text-warn border-warn/30",
  gold: "bg-gold/10 text-gold border-gold/30",
};

export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${badgeTones[tone]}`}>
      {children}
    </span>
  );
}

export function StatCard({
  label,
  value,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: number | string;
  icon?: ReactNode;
  tone?: BadgeTone;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</p>
          <p className="mt-1.5 text-2xl font-bold text-ink">{value}</p>
        </div>
        {icon && <div className={`rounded-lg p-2.5 ${badgeTones[tone]} border`}>{icon}</div>}
      </div>
    </Card>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-surface-raised ${className}`} />;
}

export function EmptyState({ title, description, icon }: { title: string; description?: string; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {icon && <div className="mb-3 text-ink-faint">{icon}</div>}
      <p className="text-sm font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-ink-muted">{description}</p>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-sm font-semibold text-danger">Something went wrong</p>
      <p className="mt-1 max-w-sm text-sm text-ink-muted">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="focus-ring mt-4 rounded-lg bg-surface-raised px-4 py-2 text-sm font-medium text-ink hover:bg-surface-border">
          Try again
        </button>
      )}
    </div>
  );
}
